import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import { Alert, Box, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { PortalApi } from './portalApi';
import { portalErrorMessage } from './portalHttp';

// ---- P1-B customer portal ----
type Mode = 'accept' | 'reset';

/** The one-time token rides in the URL fragment (never in server logs or Referer); it is read here and posted. */
const TokenPasswordPage: React.FC<{ mode: Mode }> = ({ mode }) => {
  const { code = '' } = useParams<{ code: string }>();
  const location = useLocation();
  const [token] = useState(() => location.hash.replace(/^#/, ''));
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Drop the token from the address bar once it is held in state.
    if (token && window.location.hash) window.history.replaceState(null, '', window.location.pathname);
  }, [token]);

  const mismatch = confirm !== '' && confirm !== password;
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'accept') await PortalApi.acceptInvite(code, token, password);
      else await PortalApi.resetPassword(code, token, password);
      setDone(true);
    } catch (err) {
      setError(portalErrorMessage(err, 'This link is invalid or has expired. Ask the business that invited you for a new one.'));
    } finally {
      setBusy(false);
    }
  };

  const title = mode === 'accept' ? 'Set your password' : 'Choose a new password';
  if (done) {
    return (
      <Stack spacing={2}>
        <Alert severity="success">{mode === 'accept' ? 'Your password is set.' : 'Your password was changed.'} You can sign in now.</Alert>
        <Link component={RouterLink} to={`/portal/${encodeURIComponent(code)}/login`}>Go to sign in</Link>
      </Stack>
    );
  }
  return (
    <Box component="form" onSubmit={submit}>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      <Stack spacing={2}>
        {!token && <Alert severity="warning">This link is incomplete. Open the full link you were sent, or ask the business that invited you for a new one.</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label="New password" type="password" autoComplete="new-password" required fullWidth value={password}
          onChange={e => setPassword(e.target.value)} />
        <TextField label="Confirm password" type="password" autoComplete="new-password" required fullWidth value={confirm}
          error={mismatch} helperText={mismatch ? 'Passwords do not match' : undefined} onChange={e => setConfirm(e.target.value)} />
        <Button type="submit" variant="contained" disabled={busy || !token || !password || password !== confirm}>Save password</Button>
      </Stack>
    </Box>
  );
};

const AcceptInvite: React.FC = () => <TokenPasswordPage mode="accept" />;
export const ResetPassword: React.FC = () => <TokenPasswordPage mode="reset" />;
export default AcceptInvite;
// ---- end P1-B ----
